import { useCallback, useEffect, useState } from 'react';

export const ACCOMMODATION_PROPERTY_LAYOUT_STORAGE_KEY = 'accommodationPropertyDetailsLayout';
const ACCOMMODATION_PROPERTY_LAYOUT_CHANGE_EVENT = 'accommodationPropertyDetailsLayoutChange';

export const ACCOMMODATION_PROPERTY_LAYOUTS = {
  CLASSIC: 'classic',
  SIDEBAR: 'sidebar',
} as const;

export type AccommodationPropertyLayout =
  (typeof ACCOMMODATION_PROPERTY_LAYOUTS)[keyof typeof ACCOMMODATION_PROPERTY_LAYOUTS];

const DEFAULT_ACCOMMODATION_PROPERTY_LAYOUT = ACCOMMODATION_PROPERTY_LAYOUTS.CLASSIC;

export function isAccommodationPropertyLayout(value: unknown): value is AccommodationPropertyLayout {
  return value === ACCOMMODATION_PROPERTY_LAYOUTS.CLASSIC || value === ACCOMMODATION_PROPERTY_LAYOUTS.SIDEBAR;
}

function readStoredAccommodationPropertyLayout(): AccommodationPropertyLayout {
  try {
    const item = window.localStorage.getItem(ACCOMMODATION_PROPERTY_LAYOUT_STORAGE_KEY);
    const parsed = item ? JSON.parse(item) : DEFAULT_ACCOMMODATION_PROPERTY_LAYOUT;

    return isAccommodationPropertyLayout(parsed) ? parsed : DEFAULT_ACCOMMODATION_PROPERTY_LAYOUT;
  } catch (error) {
    console.warn(`Error reading localStorage key "${ACCOMMODATION_PROPERTY_LAYOUT_STORAGE_KEY}":`, error);
    return DEFAULT_ACCOMMODATION_PROPERTY_LAYOUT;
  }
}

export default function useAccommodationPropertyLayoutPreference() {
  const [propertyLayout, setStoredPropertyLayout] = useState<AccommodationPropertyLayout>(
    readStoredAccommodationPropertyLayout,
  );

  const setPropertyLayout = useCallback((layout: AccommodationPropertyLayout) => {
    try {
      window.localStorage.setItem(ACCOMMODATION_PROPERTY_LAYOUT_STORAGE_KEY, JSON.stringify(layout));
    } catch (error) {
      console.warn(`Error setting localStorage key "${ACCOMMODATION_PROPERTY_LAYOUT_STORAGE_KEY}":`, error);
    }

    setStoredPropertyLayout(layout);
    window.dispatchEvent(
      new CustomEvent<AccommodationPropertyLayout>(ACCOMMODATION_PROPERTY_LAYOUT_CHANGE_EVENT, {
        detail: layout,
      }),
    );
  }, []);

  useEffect(() => {
    const syncPropertyLayout = () => {
      setStoredPropertyLayout(readStoredAccommodationPropertyLayout());
    };

    const handleLayoutChangeEvent = (event: Event) => {
      const { detail } = event as CustomEvent<AccommodationPropertyLayout>;

      setStoredPropertyLayout(isAccommodationPropertyLayout(detail) ? detail : readStoredAccommodationPropertyLayout());
    };

    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === ACCOMMODATION_PROPERTY_LAYOUT_STORAGE_KEY) {
        syncPropertyLayout();
      }
    };

    window.addEventListener(ACCOMMODATION_PROPERTY_LAYOUT_CHANGE_EVENT, handleLayoutChangeEvent);
    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener(ACCOMMODATION_PROPERTY_LAYOUT_CHANGE_EVENT, handleLayoutChangeEvent);
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, []);

  return [propertyLayout, setPropertyLayout] as const;
}
